import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Leaf,
  ShieldCheck,
  LineChart,
  Layers,
  Database,
  Compass,
  Target,
  Activity,
  FileBarChart,
  Send,
  MapPin,
  Wind,
  Sun,
  FlaskConical,
} from "lucide-react";

import heroImg from "../assets/images/hero.jpg";
import solarImg from "../assets/images/solar-farm.jpg";
import windImg from "../assets/images/wind-turbines.jpg";
import industrialImg from "../assets/images/industrial-plant.jpg";
import opportunitiesImg from "../assets/images/opportunities.jpg";
import lithiumOreImg from "../assets/images/lithium-ore.jpg";

const WHY_ICONS = [Database, Layers, Compass, Target];
const HOW_ICONS = [Compass, Target, Activity, FileBarChart];
const SECTOR_ICONS = [FlaskConical, Wind, Sun, Leaf];

export default function Home() {
  const { t } = useTranslation();
  const { lang = "fr" } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", consent: false });
  const [error, setError] = useState("");

  const whyItems = t("home.why.items", { returnObjects: true }) || [];
  const howSteps = t("home.how.steps", { returnObjects: true }) || [];
  const sectorItems = t("home.sectors.items", { returnObjects: true }) || [];
  const pillars = t("home.transparency.pillars", { returnObjects: true }) || [];

  const onSubmit = (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.consent) {
      setError(lang === "fr"
        ? "Merci de renseigner votre nom, votre e-mail et d'accepter la politique."
        : "Please provide your name, email and accept the policy.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError(lang === "fr" ? "Adresse e-mail invalide." : "Invalid email address.");
      return;
    }
    // No backend — just redirect to the required French-only Thank You page
    navigate("/thank_you");
  };

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="vx-hero" data-testid="hero-section">
        <div
          className="vx-hero-overlay"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(10,25,47,0.72) 0%, rgba(10,25,47,0.92) 100%), url(${heroImg})`,
          }}
        />
        <div className="vx-hero-grid" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="vx-eyebrow" style={{ color: "#34d399" }}>{t("home.eyebrow")}</div>
              <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight" style={{ color: "white" }}>
                {t("home.hero_title")}
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-200 leading-relaxed max-w-3xl">
                {t("home.hero_subtitle")}
              </p>
              <p className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed max-w-3xl">
                {t("home.hero_body")}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#newsletter"
                  className="vx-btn-primary"
                  data-testid="hero-cta-primary"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("newsletter")?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  <Send size={16} /> {t("cta.primary")}
                </a>
                <a
                  href="#how"
                  className="vx-btn-ghost-light"
                  data-testid="hero-cta-secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("how")?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  {t("cta.secondary")}
                </a>
              </div>
            </div>
            <div className="lg:col-span-4 grid grid-cols-2 gap-3" data-testid="hero-metrics">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="vx-card-dark" style={{ padding: "1.25rem" }}>
                  <div className="vx-num text-3xl">
                    {t(`home.hero_metrics.${["one", "two", "three", "four"][n-1]}_value`)}
                  </div>
                  <div className="mt-2 text-xs uppercase tracking-wider text-slate-300">
                    {t(`home.hero_metrics.${["one", "two", "three", "four"][n-1]}_label`)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section id="why" className="py-20 md:py-28" style={{ backgroundColor: "var(--vx-white)" }} data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="vx-eyebrow">— 01 · {lang === "fr" ? "Opportunités" : "Opportunities"}</div>
              <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight" style={{ color: "var(--vx-navy)" }}>
                {t("home.why.title")}
              </h2>
              <p className="mt-4 text-base leading-relaxed" style={{ color: "var(--vx-graphite)" }}>
                {t("home.why.subtitle")}
              </p>
              <div className="mt-8 aspect-[4/5] vx-photo hidden lg:block" style={{ backgroundImage: `url(${opportunitiesImg})` }} aria-label="natural resources landscape" />
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {whyItems.map((item, i) => {
                const Icon = WHY_ICONS[i] || Database;
                return (
                  <div key={i} className="vx-card h-full" data-testid={`why-card-${i}`}>
                    <div className="inline-flex items-center justify-center w-10 h-10" style={{ backgroundColor: "var(--vx-emerald-light)", color: "var(--vx-emerald-dark)" }}>
                      <Icon size={20} />
                    </div>
                    <h3 className="mt-4 font-serif-display text-xl font-semibold" style={{ color: "var(--vx-navy)" }}>{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--vx-graphite)" }}>{item.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="how" className="py-20 md:py-28" style={{ backgroundColor: "var(--vx-bg)" }} data-testid="how-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="vx-eyebrow">— 02 · {lang === "fr" ? "Processus" : "Process"}</div>
            <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight" style={{ color: "var(--vx-navy)" }}>
              {t("home.how.title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed" style={{ color: "var(--vx-graphite)" }}>
              {t("home.how.subtitle")}
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {howSteps.map((s, i) => {
              const Icon = HOW_ICONS[i] || Compass;
              return (
                <div key={i} className="vx-card h-full" data-testid={`how-step-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="vx-step-num">STEP · 0{i + 1}</span>
                    <Icon size={22} style={{ color: "var(--vx-emerald)" }} />
                  </div>
                  <h3 className="mt-4 font-serif-display text-lg font-semibold" style={{ color: "var(--vx-navy)" }}>{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--vx-graphite)" }}>{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section className="py-20 md:py-28" style={{ backgroundColor: "var(--vx-white)" }} data-testid="sectors-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl">
              <div className="vx-eyebrow">— 03 · {lang === "fr" ? "Filières" : "Sectors"}</div>
              <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight" style={{ color: "var(--vx-navy)" }}>
                {t("home.sectors.title")}
              </h2>
              <p className="mt-4 text-base" style={{ color: "var(--vx-graphite)" }}>{t("home.sectors.subtitle")}</p>
            </div>
            <div className="flex items-center gap-2 vx-eyebrow" style={{ color: "var(--vx-emerald)" }}>
              <MapPin size={14} /> Allier · Bretagne · Occitanie · Grand Est
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {sectorItems.map((s, i) => {
              const Icon = SECTOR_ICONS[i] || Leaf;
              const photos = [lithiumOreImg, windImg, solarImg, industrialImg];
              return (
                <div key={i} className="vx-card overflow-hidden" style={{ padding: 0 }} data-testid={`sector-${i}`}>
                  <div className="aspect-[16/10] vx-photo" style={{ backgroundImage: `url(${photos[i]})`, borderRadius: 0, border: 0 }} />
                  <div className="p-5">
                    <div className="flex items-center gap-2">
                      <Icon size={18} style={{ color: "var(--vx-emerald)" }} />
                      <div className="text-xs font-mono-data uppercase tracking-wider" style={{ color: "var(--vx-emerald-dark)" }}>0{i+1}</div>
                    </div>
                    <h3 className="mt-3 font-serif-display text-lg font-semibold" style={{ color: "var(--vx-navy)" }}>{s.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed" style={{ color: "var(--vx-graphite)" }}>{s.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section id="transparency" className="py-20 md:py-28" style={{ backgroundColor: "var(--vx-navy)", color: "white" }} data-testid="transparency-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="vx-eyebrow" style={{ color: "#34d399" }}>— 04 · {lang === "fr" ? "Gouvernance" : "Governance"}</div>
              <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white">
                {t("home.transparency.title")}
              </h2>
              <p className="mt-5 text-base sm:text-lg text-slate-200 leading-relaxed">{t("home.transparency.body")}</p>
              <p className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed">{t("home.transparency.footer")}</p>
            </div>
            <div className="lg:col-span-5 space-y-3">
              {pillars.map((p, i) => (
                <div key={i} className="vx-card-dark" data-testid={`pillar-${i}`}>
                  <div className="flex items-start gap-3">
                    <ShieldCheck size={22} style={{ color: "#34d399" }} className="mt-0.5" />
                    <div>
                      <h3 className="text-white font-semibold">{p.title}</h3>
                      <p className="mt-1 text-sm text-slate-300">{p.body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" className="py-20 md:py-28 vx-newsletter" data-testid="newsletter-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <div className="vx-eyebrow" style={{ color: "#34d399" }}>{t("home.newsletter.eyebrow")}</div>
              <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white">
                {t("home.newsletter.title")}
              </h2>
              <p className="mt-4 text-base text-slate-200 leading-relaxed">{t("home.newsletter.subtitle")}</p>
              <div className="mt-6 flex items-center gap-3 text-xs vx-eyebrow" style={{ color: "#34d399" }}>
                <LineChart size={14} /> {lang === "fr" ? "Analyses trimestrielles" : "Quarterly insights"}
              </div>
            </div>
            <div className="lg:col-span-6">
              <form onSubmit={onSubmit} className="vx-card-dark p-6 sm:p-8" data-testid="newsletter-form" noValidate>
                <label className="block text-xs uppercase tracking-wider text-slate-300 mb-1.5" htmlFor="nl-name">
                  {t("home.newsletter.name_label")}
                </label>
                <input
                  id="nl-name"
                  data-testid="newsletter-name"
                  type="text"
                  className="vx-input"
                  placeholder={t("home.newsletter.name_placeholder")}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <label className="block mt-4 text-xs uppercase tracking-wider text-slate-300 mb-1.5" htmlFor="nl-email">
                  {t("home.newsletter.email_label")}
                </label>
                <input
                  id="nl-email"
                  data-testid="newsletter-email"
                  type="email"
                  className="vx-input"
                  placeholder={t("home.newsletter.email_placeholder")}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
                <label className="mt-4 flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 w-4 h-4 accent-emerald-500"
                    checked={form.consent}
                    onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                    data-testid="newsletter-consent"
                  />
                  <span>{t("home.newsletter.consent")}</span>
                </label>
                {error && (
                  <div className="mt-3 text-sm" style={{ color: "#fca5a5" }} data-testid="newsletter-error">{error}</div>
                )}
                <button type="submit" className="vx-btn-primary mt-5 w-full justify-center" data-testid="newsletter-submit">
                  <Send size={16} /> {t("home.newsletter.submit")}
                </button>
                <div className="mt-3 text-xs text-slate-400">{t("home.newsletter.note")}</div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
